import React from 'react';
import type { AIAgentResponse } from '@troia-v3/types';
import { IntegrationIcon } from '@/shared/icons';

/**
 * Visuais do mockup ai-agents.html compartilhados entre card, linha de tabela
 * e KPIs: score de qualidade (`.ag-score`) e círculos de canais (`.ag-chs`).
 */

/** Narrowing local até o release do @troia-v3/types (qualityScore já no source) */
export type AgentWithScore = AIAgentResponse & { qualityScore?: number };

/** Item mínimo do dropdown de canais usado pra resolver providerId/nome */
export interface AgentChannelInfo {
  id: string;
  name: string;
  providerId?: string;
}

/** Faixas do mockup (scCls): ≥85 verde · ≥70 âmbar · <70 vermelho */
export const scoreColor = (score: number): string =>
  score >= 85
    ? 'var(--theme-success-fg, #16a34a)'
    : score >= 70
      ? 'var(--theme-warning-fg, #b45309)'
      : 'var(--theme-error-fg, #dc2626)';

/** Canais vinculados = união dos channelIds dos gatilhos de mensagem */
export const agentChannelIds = (agent: AIAgentResponse): string[] => {
  const received = agent.triggers?.message_received?.conditions?.channelIds ?? [];
  const sent = agent.triggers?.message_sent?.conditions?.channelIds ?? [];
  return [...new Set([...received, ...sent])];
};

/** `.ag-score` do mockup: barra 44×6 (fill colorido por faixa) + % bold */
export const AgentScore: React.FC<{ score: number; showBar?: boolean }> = ({ score, showBar = true }) => {
  const color = scoreColor(score);
  return (
    <span className="inline-flex items-center gap-2 font-bold text-sm tabular-nums" style={{ color }}>
      {showBar && (
        <span
          className="w-11 h-1.5 rounded-full overflow-hidden flex-none"
          style={{ backgroundColor: 'var(--theme-surface-sunken, rgba(148,163,184,.16))' }}
        >
          <span
            className="block h-full rounded-full"
            style={{ width: `${Math.min(score, 100)}%`, backgroundColor: color }}
          />
        </span>
      )}
      {score}%
    </span>
  );
};

/** `.ag-chs` do mockup: círculos 22px (bg sunken) com o ícone de marca do canal */
export const AgentChannelIcons: React.FC<{
  channelIds: string[];
  channelsById: Map<string, AgentChannelInfo>;
}> = ({ channelIds, channelsById }) => {
  if (channelIds.length === 0) return null;
  const shown = channelIds.slice(0, 4);
  return (
    <span className="flex items-center gap-1 flex-none">
      {shown.map((id) => {
        const channel = channelsById.get(id);
        return (
          <span
            key={id}
            title={channel?.name ?? 'Canal removido'}
            className="w-[22px] h-[22px] rounded-full inline-flex items-center justify-center"
            style={{ backgroundColor: 'var(--theme-surface-sunken, rgba(148,163,184,.16))' }}
          >
            <IntegrationIcon id={channel?.providerId ?? 'unknown'} size={13} variant="brand" />
          </span>
        );
      })}
      {channelIds.length > 4 && (
        <span className="text-[11px] font-semibold" style={{ color: 'var(--theme-muted-text)' }}>
          +{channelIds.length - 4}
        </span>
      )}
    </span>
  );
};
